import { useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Snackbar, TextField, Typography,
} from '@mui/material'
import { Plus } from 'lucide-react'
import { useNavigate } from '../lib/router'
import { AppShell, Crumb, HomeCrumb } from '../components/AppShell'
import { useCountries, useCreateRegion, useRegions } from '../api/hooks'
import { EmptyState, Eyebrow, FieldLabel, StatusPill, Surface } from '../components/primitives'
import { SkeletonCards } from '../components/Skeleton'
import { CountryPicker } from '../components/CountryPicker'
import { Flag } from '../components/Flag'
import { formatDate, ha, num } from '../lib/format'
import { tokens } from '../theme'

/** One row's columns, shared with the placeholder so the list does not reflow. */
const GRID = 'minmax(0,1.6fr) minmax(0,1.2fr) 130px 130px 150px'

/**
 * Regions: the named sets of plots this organisation is working on.
 *
 * A region is assembled rather than uploaded. Plots come from any number of
 * files, or are drawn, and the set is corrected as the work goes on.
 */
export function Regions() {
  const navigate = useNavigate()
  const { data: regions = [], isLoading } = useRegions()
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const actions = (
    <Button size="small" startIcon={<Plus size={14} strokeWidth={1.5} />} onClick={() => setCreating(true)}>
      New region
    </Button>
  )

  return (
    <AppShell
      crumbs={[<HomeCrumb key="a" />, <Crumb key="b">Regions</Crumb>]}
      actions={actions}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', px: 8, py: 7 }}>
        <Eyebrow sx={{ mb: 2 }}>Supply chain</Eyebrow>
        <Typography variant="h1" sx={{ mb: 5 }}>Regions</Typography>

        {/*
          While the list is on its way, rows the size of real ones — not a
          blank page that turns into "No regions yet" and then into a list.
        */}
        {isLoading && <SkeletonCards columns={GRID} />}

        {!isLoading && regions.length === 0 && (
          <EmptyState
            title="No regions yet"
            body="A region is a named set of plots you build up over time — from an upload, from polygons you draw, or from both. Create one to start."
            action={
              <Button size="small" startIcon={<Plus size={14} strokeWidth={1.5} />} onClick={() => setCreating(true)}>
                New region
              </Button>
            }
          />
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {regions.map((r) => (
            <Surface
              key={r.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/regions/${r.id}`)}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/regions/${r.id}`)}
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID,
                alignItems: 'center', gap: 5, px: 5, py: 4, cursor: 'pointer',
                transition: `background ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
                '&:hover': { backgroundColor: tokens.color.ink50 },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ font: `500 15px ${tokens.font.sans}`, color: 'text.primary' }}>
                  {r.name}
                </Typography>
                {r.description && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary', display: 'block',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}
                  >
                    {r.description}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                {r.country_code && <Flag code={r.country_code} size={13} />}
                <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                  {[r.supplier_name, r.country_name ?? r.country_code]
                    .filter(Boolean).join(' · ') || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ font: `500 15px ${tokens.font.sans}`, fontVariantNumeric: 'tabular-nums' }}>
                  {num(r.plot_count)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>polygons</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                {ha(r.area_ha, 0)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', justifyContent: 'flex-end' }}>
                {r.rejected > 0 && <StatusPill status="rejected" label={`${num(r.rejected)} rejected`} />}
                {r.flagged > 0 && <StatusPill status="flagged" label={`${num(r.flagged)} flagged`} />}
                {r.rejected === 0 && r.flagged === 0 && (
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    {formatDate(r.updated_at)}
                  </Typography>
                )}
              </Box>
            </Surface>
          ))}
        </Box>
      </Box>

      <NewRegionDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => navigate(`/regions/${id}`)}
        onError={setError}
      />
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={8000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="outlined" sx={{ backgroundColor: 'background.paper' }}>
          {error}
        </Alert>
      </Snackbar>
    </AppShell>
  )
}

function NewRegionDialog({
  open, onClose, onCreated, onError,
}: {
  open: boolean
  onClose: () => void
  onCreated: (id: string) => void
  onError: (message: string) => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [country, setCountry] = useState('')
  const { data: countries = [] } = useCountries()
  const create = useCreateRegion()

  const submit = () => {
    if (!name.trim()) return
    create.mutate(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        country_code: country || undefined,
      },
      {
        onSuccess: (region) => {
          setName('')
          setDescription('')
          setCountry('')
          onClose()
          onCreated(region.id)
        },
        onError: (e) => onError((e as Error).message),
      },
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ font: `700 20px ${tokens.font.display}` }}>New region</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <FieldLabel>Name</FieldLabel>
        <TextField
          size="small"
          fullWidth
          autoFocus
          value={name}
          placeholder="Soubré intake 2026"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          inputProps={{ 'aria-label': 'Region name' }}
          sx={{ mb: 4 }}
        />
        <FieldLabel>Description</FieldLabel>
        <TextField
          size="small"
          fullWidth
          value={description}
          placeholder="What this set of plots is for."
          onChange={(e) => setDescription(e.target.value)}
          inputProps={{ 'aria-label': 'Region description' }}
          sx={{ mb: 4 }}
        />
        <FieldLabel>Country</FieldLabel>
        <CountryPicker
          countries={countries}
          value={country}
          onChange={setCountry}
          emptyLabel="Left empty, it is read from the plots themselves."
        />
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1.5 }}>
          Decides which border the within-country check judges these plots
          against, and which national yield benchmark they are compared with.
        </Typography>
        {country && !countries.find((c) => c.code === country)?.has_boundary && (
          <Alert severity="info" sx={{ mt: 2.5 }}>
            No boundary is loaded for this country, so the within-country
            check cannot judge its plots — they will not be found outside a
            border, which is not the same as being inside one. Every other
            check runs as normal.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 6, pb: 5 }}>
        <Button size="small" variant="outlined" onClick={onClose}>Cancel</Button>
        <Button size="small" disabled={!name.trim() || create.isPending} onClick={submit}>
          {create.isPending ? 'Creating…' : 'Create region'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
